<template>
  <div class="qr-collector">
    <!-- Loading state -->
    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Loading...</p>
    </div>

    <!-- Invalid or blank (no hashId) -->
    <div v-else-if="!isValid" class="blank"></div>

    <!-- Already responded message -->
    <div v-else-if="alreadyResponded" class="message-container">
      <div class="icon">
        <i class="fas fa-check-circle"></i>
      </div>
      <h2>Already Submitted</h2>
      <p>You have already answered.</p>
      <p class="contact-info">
        Please contact<br />
        <a href="mailto:communications@bigpropertymanagement.com">
          communications@bigpropertymanagement.com
        </a>
        for help.
      </p>
    </div>

    <!-- Thank you message -->
    <div v-else-if="showThankYou" class="message-container">
      <div class="icon success">
        <i class="fas fa-check-circle"></i>
      </div>
      <h2>Thank You!</h2>
      <p>Thank you for your response.</p>
      <p class="contact-info">
        Please contact
        <a href="mailto:communications@bigpropertymanagement.com">
          communications@bigpropertymanagement.com
        </a>
        for any follow up questions.
      </p>
    </div>

    <!-- Confirmation dialog -->
    <div v-else-if="showConfirmation" class="confirmation-container">
      <h2>Please Confirm</h2>
      <p class="confirm-intro">Please review your information:</p>
      <div class="confirm-details">
        <div class="detail-row">
          <span class="label">First Name:</span>
          <span class="value">{{ formData.firstName }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Last Name:</span>
          <span class="value">{{ formData.lastName }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Phone:</span>
          <span class="value">{{ formatPhone(formData.phone) }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Apartment:</span>
          <span class="value">{{ formData.apartment }}</span>
        </div>
        <div class="detail-row">
          <span class="label">{{ response1Label }}:</span>
          <span class="value">{{ formData.response1 }}</span>
        </div>
        <div v-if="formData.comment" class="detail-row">
          <span class="label">Comment:</span>
          <span class="value">{{ formData.comment }}</span>
        </div>
      </div>
      <div class="button-group">
        <button @click="confirmSubmit" class="btn-primary">Confirm</button>
        <button @click="showConfirmation = false" class="btn-secondary">
          Go Back
        </button>
      </div>
      <div v-if="submitError" class="error-message">{{ submitError }}</div>
    </div>

    <!-- Data collection form -->
    <div v-else class="form-container">
      <!-- Fixed Header -->
      <div class="form-header">
        <div class="title-bar">
          <h2>{{ campaignTitle || 'Data Collection' }}</h2>
        </div>
        <p v-if="campaignDescription" class="campaign-description">
          {{ campaignDescription }}
        </p>
      </div>

      <!-- Scrollable Form Fields -->
      <div class="form-content">
        <form @submit.prevent="handleSubmit">
          <div class="form-row">
            <div class="form-group half-width">
              <label for="firstName">First Name *</label>
              <input
                id="firstName"
                v-model="formData.firstName"
                type="text"
                required
                maxlength="100"
                :class="{ error: errors.firstName }"
                @input="clearError('firstName')"
              />
              <span v-if="errors.firstName" class="error-text">{{
                errors.firstName
              }}</span>
            </div>

            <div class="form-group half-width">
              <label for="lastName">Last Name *</label>
              <input
                id="lastName"
                v-model="formData.lastName"
                type="text"
                required
                maxlength="100"
                :class="{ error: errors.lastName }"
                @input="clearError('lastName')"
              />
              <span v-if="errors.lastName" class="error-text">{{
                errors.lastName
              }}</span>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group half-width">
              <label for="phone">Phone (10 digits) *</label>
              <input
                id="phone"
                v-model="formData.phone"
                type="tel"
                required
                maxlength="10"
                placeholder="1234567890"
                :class="{ error: errors.phone }"
                @input="clearError('phone')"
              />
              <span v-if="errors.phone" class="error-text">{{
                errors.phone
              }}</span>
            </div>

            <div class="form-group half-width">
              <label for="apartment">Apartment *</label>
              <input
                id="apartment"
                v-model="formData.apartment"
                type="text"
                required
                maxlength="50"
                :class="{ error: errors.apartment }"
                @input="clearError('apartment')"
              />
              <span v-if="errors.apartment" class="error-text">{{
                errors.apartment
              }}</span>
            </div>
          </div>

          <div class="form-group">
            <label for="response1">{{ response1Label }} *</label>
            <input
              id="response1"
              v-model="formData.response1"
              type="text"
              required
              maxlength="100"
              :class="{ error: errors.response1 }"
              @input="clearError('response1')"
            />
            <span v-if="errors.response1" class="error-text">{{
              errors.response1
            }}</span>
          </div>

          <div class="form-group">
            <label for="comment">Comment (optional)</label>
            <textarea
              id="comment"
              v-model="formData.comment"
              rows="3"
              maxlength="1000"
              :class="{ error: errors.comment }"
              @input="clearError('comment')"
            ></textarea>
            <span v-if="errors.comment" class="error-text">{{
              errors.comment
            }}</span>
          </div>
        </form>
      </div>

      <!-- Fixed Submit Button with Bottom Margin -->
      <div class="form-footer">
        <button @click="handleSubmit" class="btn-primary btn-submit">
          Submit
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// Use axiosConfig to get the correct baseURL (works in both dev and production)
import axios from '@/axiosConfig';

export default {
  name: 'QRCollector',
  data() {
    return {
      hashId: null,
      isValid: false,
      isLoading: true,
      alreadyResponded: false,
      campaignTitle: '',
      campaignDescription: '',
      response1Label: 'Tag Number', // Dynamic label from campaign
      formData: {
        firstName: '',
        lastName: '',
        phone: '',
        apartment: '',
        response1: '',
        comment: '',
      },
      errors: {},
      showConfirmation: false,
      showThankYou: false,
      submitError: null,
    };
  },

  async mounted() {
    // Extract hashId from URL query parameter
    this.hashId = this.$route.query.id;

    // If no hashId, just show blank page
    if (!this.hashId) {
      this.isLoading = false;
      return;
    }

    // Validate the hashId
    await this.validateCampaign();
  },

  methods: {
    async validateCampaign() {
      try {
        const response = await axios.get(
          `/api/public/collector/validate/${this.hashId}`,
        );

        // Check if we got a blank response (invalid hash)
        if (!response.data || response.data === '') {
          this.isValid = false;
          this.isLoading = false;
          return;
        }

        if (response.data.valid) {
          this.isValid = true;
          this.campaignTitle = response.data.campaign?.title || '';
          this.campaignDescription = response.data.campaign?.description || '';
          this.response1Label =
            response.data.campaign?.response_1_label || 'Tag Number';
          this.alreadyResponded = response.data.alreadyResponded || false;
        } else {
          this.isValid = false;
        }
      } catch (error) {
        console.error('Validation error:', error);
        this.isValid = false;
      } finally {
        this.isLoading = false;
      }
    },

    validateForm() {
      this.errors = {};
      let isValid = true;

      // Validate first name
      const nameRegex = /^[a-zA-Z\s'-]{1,100}$/;
      if (!this.formData.firstName.trim()) {
        this.errors.firstName = 'First name is required';
        isValid = false;
      } else if (!nameRegex.test(this.formData.firstName)) {
        this.errors.firstName = 'Invalid characters in first name';
        isValid = false;
      }

      // Validate last name
      if (!this.formData.lastName.trim()) {
        this.errors.lastName = 'Last name is required';
        isValid = false;
      } else if (!nameRegex.test(this.formData.lastName)) {
        this.errors.lastName = 'Invalid characters in last name';
        isValid = false;
      }

      // Validate phone (exactly 10 digits)
      const phoneRegex = /^\d{10}$/;
      if (!this.formData.phone.trim()) {
        this.errors.phone = 'Phone number is required';
        isValid = false;
      } else if (!phoneRegex.test(this.formData.phone)) {
        this.errors.phone = 'Phone must be exactly 10 digits';
        isValid = false;
      }

      // Validate apartment
      const apartmentRegex = /^[a-zA-Z0-9\s#-]{1,50}$/;
      if (!this.formData.apartment.trim()) {
        this.errors.apartment = 'Apartment number is required';
        isValid = false;
      } else if (!apartmentRegex.test(this.formData.apartment)) {
        this.errors.apartment = 'Invalid characters in apartment number';
        isValid = false;
      }

      // Validate response_1
      const response1Regex = /^[a-zA-Z0-9\s-]{1,100}$/;
      if (!this.formData.response1.trim()) {
        this.errors.response1 = `${this.response1Label} is required`;
        isValid = false;
      } else if (!response1Regex.test(this.formData.response1)) {
        this.errors.response1 = `Invalid characters in ${this.response1Label}`;
        isValid = false;
      }

      // Validate comment if provided
      if (this.formData.comment && this.formData.comment.length > 1000) {
        this.errors.comment = 'Comment is too long (max 1000 characters)';
        isValid = false;
      }

      return isValid;
    },

    handleSubmit() {
      if (this.validateForm()) {
        // Check if phone has already been used before showing confirmation
        this.checkPhoneAndShowConfirmation();
      }
    },

    async checkPhoneAndShowConfirmation() {
      try {
        const response = await axios.get(
          `/api/public/collector/validate/${this.hashId}`,
          {
            params: { phone: this.formData.phone },
          },
        );

        if (response.data.alreadyResponded) {
          this.alreadyResponded = true;
        } else {
          this.showConfirmation = true;
        }
      } catch (error) {
        console.error('Error checking phone:', error);
        this.showConfirmation = true; // Show confirmation anyway
      }
    },

    async confirmSubmit() {
      this.submitError = null;

      try {
        await axios.post('/api/public/collector/submit', {
          hashId: this.hashId,
          firstName: this.formData.firstName.trim(),
          lastName: this.formData.lastName.trim(),
          phone: this.formData.phone.trim(),
          apartment: this.formData.apartment.trim(),
          response1: this.formData.response1.trim(),
          comment: this.formData.comment.trim() || null,
        });

        // Success - show thank you message
        this.showConfirmation = false;
        this.showThankYou = true;
      } catch (error) {
        console.error('Submit error:', error);
        if (error.response?.data?.error) {
          this.submitError = error.response.data.error;
        } else {
          this.submitError = 'An error occurred. Please try again.';
        }
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },

    formatPhone(phone) {
      if (!phone || phone.length !== 10) return phone;
      return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
    },
  },
};
</script>

<style scoped>
.qr-collector {
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
  padding: 20px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu,
    Cantarell, sans-serif;
  display: flex;
  align-items: top;
  justify-content: center;
}

.blank {
  height: 100vh;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  color: white;
  font-size: 1.2em;
}

.loading i {
  font-size: 3em;
  margin-bottom: 20px;
}

.form-container,
.message-container,
.confirmation-container {
  max-width: 480px;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 40px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Fixed Header */
.form-header {
  flex-shrink: 0;
  padding: 0;
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

.title-bar {
  background: #a080a8;
  border-radius: 16px 16px 0 0;
  padding: 0;
  margin: 0;
}

.title-bar h2 {
  margin: 0;
  padding: 20px 30px;
  color: white;
  font-size: 1.8em;
  text-align: center;
}

.campaign-description {
  padding: 8px 30px 4px 30px;
  text-align: left;
  color: #666;
  margin: 0;
  background: white;
  font-size: 0.85em;
}

.form-header .form-intro {
  padding: 15px 30px;
  text-align: center;
  color: #666;
  margin: 0;
  background: white;
}

/* Scrollable Content */
.form-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px 30px 30px 30px;
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.form-content::-webkit-scrollbar {
  display: none;
}

/* Fixed Footer with Bottom Margin */
.form-footer {
  flex-shrink: 0;
  padding: 20px 30px;
  padding-bottom: 50px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.message-container,
.confirmation-container {
  padding: 30px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

h2 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1.8em;
  text-align: center;
}

.message-container h2,
.confirmation-container h2 {
  color: #333;
}

.form-intro,
.confirm-intro {
  text-align: center;
  color: #666;
  margin-bottom: 30px;
}

.form-row {
  display: flex;
  gap: 15px;
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 5px;
}

.form-group.half-width {
  flex: 1;
  min-width: 0;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 0.95em;
}

input,
textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: #a080a8;
}

input.error,
textarea.error {
  border-color: #f44336;
}

.error-text {
  color: #f44336;
  font-size: 0.85em;
  margin-top: 5px;
  display: block;
}

.btn-primary,
.btn-secondary {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 1.1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  min-height: 44px;
}

.btn-primary {
  background: #a080a8;
  color: white;
  width: 100%;
}

.btn-primary:hover {
  background: #8a6a8f;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(160, 128, 168, 0.4);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-secondary {
  background: #f0f0f0;
  color: #666;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

.message-container {
  text-align: center;
  padding: 40px 30px;
}

.icon {
  font-size: 4em;
  color: #f44336;
  margin-bottom: 20px;
}

.icon.success {
  color: #4caf50;
}

.message-container p {
  color: #666;
  margin: 10px 0;
  font-size: 1.1em;
}

.contact-info {
  margin-top: 30px;
  font-size: 0.95em;
}

.contact-info a {
  color: #a080a8;
  text-decoration: none;
  word-break: break-all;
}

.contact-info a:hover {
  text-decoration: underline;
}

.confirmation-container {
  padding: 30px;
}

.confirm-details {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e0e0e0;
}

.detail-row:last-child {
  border-bottom: none;
}

.label {
  font-weight: 600;
  color: #666;
}

.value {
  color: #333;
  text-align: right;
  flex: 1;
  margin-left: 20px;
  word-break: break-word;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.error-message {
  margin-top: 20px;
  padding: 15px;
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .qr-collector {
    padding: 10px;
  }

  .form-container,
  .message-container,
  .confirmation-container {
    max-height: calc(76vh - 20px);
  }

  .form-header {
    padding: 0;
  }

  .title-bar h2 {
    padding: 15px 20px;
    font-size: 1.5em;
  }

  .form-header .form-intro {
    padding: 10px 20px;
  }

  .form-content {
    padding: 15px 20px 20px 20px;
  }

  .form-footer {
    padding: 15px 20px;
  }

  h2 {
    font-size: 1.5em;
  }

  input,
  textarea {
    font-size: 16px; /* Prevent zoom on iOS */
  }
}

/* Screens 810px height and lower - use 83vh */
@media (min-width: 360px) and (max-height: 810px) and (max-width: 480px) {
  .form-container,
  .message-container,
  .confirmation-container {
    max-height: calc(90vh - 20px);
  }
}

/* Screens 915px height and larger - use 76vh */
@media (min-width: 360px) and (min-height: 915px) and (max-width: 480px) {
  .form-container,
  .message-container,
  .confirmation-container {
    max-height: calc(76vh - 20px);
  }
}
</style>
